// composables/useDirections.ts
import {
  createDirectionValidator,
  patchDirectionValidator,
  updateDirectionValidator,
} from '~/validators/direction'
import { Direction } from '~/models/Direction'
import { useApiMonOrga } from '~/composables/api/useApiMonOrga'
import { EApiError } from '~~/errors/EApiError'
import type { OrganizationKey } from '~~/errors/ConcurrentChangeError'
import type { MergeRecord } from '~~/utils/organization/concurrentPatch'
import { dirTypesFromGroupedRecord, normalizeDirTypes } from '~/utils/organization/dirTypes'

type DirectionsByDirType = Record<string, Direction[]>

interface RawDirection {
  dirLib: string
  dirLibLong?: string | null
  dirDesc?: string | null
  dirType: string
  domainsId?: string[] | null
}

type DirectionsByDirTypeApi = Record<string, RawDirection[]>

interface DirectionWithDomains {
  direction: Direction
  domainsId: string[]
}

function mapToDirection(raw: RawDirection): Direction {
  return new Direction({
    dirLib: raw.dirLib,
    dirLibLong: raw.dirLibLong ?? '',
    dirDesc: raw.dirDesc ?? '',
    dirType: raw.dirType,
  })
}

export interface UseDirectionsReturn {
  fetchDirTypes: () => Promise<string[]>
  fetchDirectionsByDirType: () => Promise<DirectionsByDirType>
  fetchDirections: () => Promise<Direction[]>
  storeDirection: (directionData: Direction) => Promise<Direction | null>
  fetchDirectionById: (id: string) => Promise<DirectionWithDomains | null>
  updateDirection: (id: string, directionData: Direction) => Promise<Direction | null>
  patchDirection: (id: string, partialData: Partial<Direction>) => Promise<Direction | null>
  mergeDirection: (
    id: string,
    changes: MergeRecord,
    base: MergeRecord
  ) => Promise<{ direction: Direction; currentKey: OrganizationKey | null } | null>
  deleteDirection: (id: string) => Promise<boolean>
}

async function fetchDirectionsByDirType(): Promise<DirectionsByDirType> {
  const data = await useApiMonOrga<DirectionsByDirTypeApi>('GET', 'directions', {
    sortByDirType: '',
  })
  if (!data || Object.keys(data).length === 0) {
    throw new EApiError('No directions found or data is not in expected format')
  }

  const directionsByDirType: DirectionsByDirType = {}
  for (const dirType of Object.keys(data)) {
    const rawList = data[dirType] ?? []
    directionsByDirType[dirType] = rawList.map(mapToDirection)
  }
  return directionsByDirType
}

async function dirTypesFromEndpoint(): Promise<string[]> {
  const data = await useApiMonOrga<unknown>('GET', 'directions/types')
  return normalizeDirTypes(data)
}

async function fetchDirTypes(): Promise<string[]> {
  const fromEndpoint = await dirTypesFromEndpoint().catch(function ignoreMissingEndpoint() {
    return [] as string[]
  })
  if (fromEndpoint.length > 0) {
    return fromEndpoint
  }

  const fromIndex = await fetchDirectionsByDirType()
    .then(dirTypesFromGroupedRecord)
    .catch(function ignoreEmptyIndex() {
      return [] as string[]
    })
  if (fromIndex.length > 0) {
    return fromIndex
  }

  throw new EApiError('Aucun type de direction n’a été renvoyé par l’API.')
}

async function fetchDirections(): Promise<Direction[]> {
  const data = await useApiMonOrga<RawDirection[]>('GET', 'directions')
  return data?.map(mapToDirection) ?? []
}

async function storeDirection(directionData: Direction): Promise<Direction | null> {
  const payload = await createDirectionValidator.validate(directionData)
  const data = await useApiMonOrga<RawDirection>('POST', 'directions', undefined, payload)
  return data ? mapToDirection(data) : null
}

async function fetchDirectionById(id: string): Promise<DirectionWithDomains | null> {
  const data = await useApiMonOrga<RawDirection>('GET', `directions/${id}`)
  if (!data) return null

  return {
    direction: mapToDirection(data),
    domainsId: data.domainsId ?? [],
  }
}

async function updateDirection(id: string, directionData: Direction): Promise<Direction | null> {
  const payload = await updateDirectionValidator.validate(directionData)
  const data = await useApiMonOrga<RawDirection>('PUT', `directions/${id}`, undefined, payload)
  return data ? mapToDirection(data) : null
}

async function patchDirection(
  id: string,
  partialData: Partial<Direction>
): Promise<Direction | null> {
  const payload = await patchDirectionValidator.validate(partialData)
  const data = await useApiMonOrga<RawDirection>('PATCH', `directions/${id}`, undefined, payload)
  return data ? mapToDirection(data) : null
}

async function mergeDirection(
  id: string,
  changes: MergeRecord,
  base: MergeRecord
): Promise<{ direction: Direction; currentKey: OrganizationKey | null } | null> {
  const validated = await patchDirectionValidator.validate(changes)
  const data = await useApiMonOrga<RawDirection & { currentKey?: OrganizationKey | null }>(
    'PATCH',
    `directions/${encodeURIComponent(id)}`,
    undefined,
    { changes: validated, base }
  )
  if (!data) return null
  return { direction: mapToDirection(data), currentKey: data.currentKey ?? null }
}

async function deleteDirection(id: string): Promise<boolean> {
  try {
    await useApiMonOrga<unknown>('DELETE', `directions/${id}`)
    return true
  } catch (error) {
    console.error(`Failed to delete direction with ID ${id}`, error)
    return false
  }
}

export function useDirections(): UseDirectionsReturn {
  return {
    fetchDirTypes,
    fetchDirectionsByDirType,
    fetchDirections,
    storeDirection,
    fetchDirectionById,
    updateDirection,
    patchDirection,
    mergeDirection,
    deleteDirection,
  }
}
